import BaseCan from "../../../common/abilities/baseCan";
import Organization from "../../../common/models/organization";
import Group from "../models/group";
import {
  OrganizationRoleCapability,
  updateOrganizationCapabilities
} from "../../../common/enums/roles/capabilities/organizationRoleCapability";
import Role from "../../../common/models/role";
import {GroupRoleCapability} from "../../../common/enums/roles/capabilities/groupRoleCapability";
import UserDecorator from "../../admin/models/userDecorator";
import User from "../../../common/models/user";

export default class OrganizationManagementCan extends BaseCan {
  constructor(user: User | UserDecorator) {
    super(user);

    this.iCan('update', Organization, organization => {
      return organization.is_editable && organization.hasCapability(updateOrganizationCapabilities);
    });

    this.iCan('manageMembers', Organization, organization => {
      return organization.hasCapability(OrganizationRoleCapability.manageMembers);
    });

    this.iCan('manageRoles', Organization, organization => {
      return organization.hasCapability(OrganizationRoleCapability.manageRoles);
    });

    this.iCan('manageAllGroups', Organization, organization => {
      return organization.hasCapability(OrganizationRoleCapability.manageAllGroups);
    });

    this.iCan('createGroups', Organization, organization => {
      return organization.hasCapability(OrganizationRoleCapability.createGroups);
    });

    this.iCan('viewGroupsArea', Organization, organization => {
      return organization.canViewGroups ||
        this.can('manageAllGroups', organization) ||
        this.can('createGroups', organization) ||
        organization.groups.some(g => this.can('viewGroup', g));
    });

    this.iCan('viewGroup', Group, group => {
      return this.can('manageAllGroups', group.organization) ||
        this.can('manageMembers', group) ||
        this.can('manageRoles', group) ||
        this.can('destroy', group);
    });

    this.iCan('manageMembers', Group, group => {
      return this.can('manageAllGroups', group.organization) ||
        group.hasCapability(GroupRoleCapability.manageMembers);
    });

    this.iCan('manageRoles', Group, group => {
      return this.can('manageAllGroups', group.organization) ||
        group.hasCapability(GroupRoleCapability.manageRoles);
    });

    this.iCan('manageMembersOrRoles', [Organization, Group], parent => {
      return this.can('manageMembers', parent) || this.can('manageRoles', parent);
    });

    this.iCan('edit', Group, group => {
      return this.can('manageAllGroups', group.organization) || this.can('manageMembers', group);
    });

    this.iCan('destroy', Group, group => {
      return this.can('manageAllGroups', group.organization) ||
        group.hasCapability(GroupRoleCapability.deleteGroup);
    });

    this.iCan('editOrDestroy', Group, group => {
      return this.can('edit', group) || this.can('destroy', group);
    });

    this.iCan(['edit', 'delete', 'modify'], Role, role => {
      return !role.is_permanent;
    });

    this.iCan('manageEntities', Organization, organization => {
      return organization.hasCapability(OrganizationRoleCapability.manageEntities);
    });

    this.iCan('manageUsers', Organization, organization => {
      return organization.hasCapability(OrganizationRoleCapability.manageUsers);
    });

    this.iCan('manageQuickLinks', Organization, organization => {
      const capabilities = [OrganizationRoleCapability.manageQuickLinksFolders, OrganizationRoleCapability.createQuickLinksFolders];
      return organization.hasCapability(capabilities);
    })
  }
}